import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAxios, useApiResponseParser } from "../";
import type { ISupplier } from "../../interfaces";

export default function useSupplier() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();

    function useCreate() {
        return useMutation({
            mutationFn: (parameters: any) =>
                axiosPrivate.post(`/v1/supplier/create`, parameters).then(parseSuccessResponseData),
        });
    }

    function useList(type?: string) {
        return useQuery<ISupplier[]>({
            queryKey: ["suppliers", type],
            queryFn: async () => {
                const response = await axiosPrivate.get("/v1/supplier/list", {
                    params: type ? { Type: type } : {},
                });
                return parseSuccessResponseData(response);
            },
        });
    }

    function useListInventory(supplierId: string) {
        return useQuery({
            queryKey: ["supplier-inventory", supplierId],
            queryFn: async () => {
                const response = await axiosPrivate.get("/v1/supplier/inventory/list", {
                    params: { SupplierId: supplierId },
                });
                return parseSuccessResponseData(response);
            },
            enabled: !!supplierId,
        });
    }

    // Mutation version for imperative fetching of supplier inventory
    function useListInventoryMutation() {
        return useMutation({
            mutationFn: (params: { SupplierId: string }) => {
                const response = axiosPrivate.get("/v1/supplier/inventory/list", {
                    params: { SupplierId: params.SupplierId },
                });
                return response.then(parseSuccessResponseData);
            },
        });
    }

    function useSupplierComparison() {
        return useMutation({
            mutationFn: (params: any) =>
                axiosPrivate.post(`/v1/supplier/comparison`, params).then(parseSuccessResponseData),
        });
    }

    function useListById() {
        return useMutation({
            mutationFn: (params: { id: string }) =>
                axiosPrivate.get(`/v1/project/list/${params.id}`).then(parseSuccessResponseData),
        });
    }

    // API for supplier inventory (materials)
    function useListInventoryMaterials() {
        return useMutation({
            mutationFn: () => axiosPrivate.get(`/v1/supplier/inventory/materials`).then(parseSuccessResponseData),
        });
    }

    function useListPriceRanges() {
        return useMutation({
            mutationFn: () => axiosPrivate.get(`/v1/supplier/inventory/price-ranges`).then(parseSuccessResponseData),
        });
    }

    // API for supplier inventory (labor)
    function useListInventoryLabor() {
        return useMutation({
            mutationFn: () => axiosPrivate.get(`/v1/supplier/inventory/labor`).then(parseSuccessResponseData),
        });
    }

    function useUpdate() {
        const queryClient = useQueryClient();
        return useMutation({
            mutationFn: (parameters: any) =>
                axiosPrivate.post(`/v1/supplier/update`, parameters).then(parseSuccessResponseData),
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["suppliers"] });
            },
        });
    }

    function useDelete() {
        const queryClient = useQueryClient();
        return useMutation({
            mutationFn: (id: string) => axiosPrivate.delete(`/v1/supplier/delete/${id}`).then(parseSuccessResponseData),
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["suppliers"] });
            },
        });
    }

    function useCreateInventory() {
        const queryClient = useQueryClient();
        return useMutation({
            mutationFn: (parameters: any) =>
                axiosPrivate.post(`/v1/supplier/inventory/create`, parameters).then(parseSuccessResponseData),
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries({ queryKey: ["supplier-inventory", variables.SupplierId] });
            },
        });
    }

    function useUpdateInventory() {
        const queryClient = useQueryClient();
        return useMutation({
            mutationFn: (parameters: any) =>
                axiosPrivate.post(`/v1/supplier/inventory/update`, parameters).then(parseSuccessResponseData),
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["supplier-inventory"] });
            },
        });
    }

    function useDeleteInventory() {
        const queryClient = useQueryClient();
        return useMutation({
            mutationFn: (id: string) =>
                axiosPrivate.delete(`/v1/supplier/inventory/delete/${id}`).then(parseSuccessResponseData),
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["supplier-inventory"] });
            },
        });
    }

    return {
        useCreate,
        useList,
        useUpdate,
        useDelete,
        useListById,
        useListInventory,
        useListInventoryMutation,
        useCreateInventory,
        useUpdateInventory,
        useDeleteInventory,
        useSupplierComparison,
        useListInventoryMaterials,
        useListPriceRanges,
        useListInventoryLabor,
    };
}
